import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MessageSquareText, UserCheck, PencilLine, ShieldCheck } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', amber: '#E8A020', amberInk: '#B27516', slate: '#5C6B7A', red: '#E24B4A', green: '#1D9E75' };
const inputCls = 'w-full px-3 py-2 text-sm rounded-sm border outline-none focus:border-[#1A4C7C] bg-white';
const inputStyle = { borderColor: 'rgba(136,150,168,0.35)', color: C.navy };

const isField = (v) => typeof v === 'string' && /^[a-z][a-z0-9_]*$/.test(v);
const titleOf = (f) => f.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// Fields the broker may correct on a returned quote: the ones the refer reasons point at.
export const amendableFields = (sub) => {
  const sd = sub.submission_data || {};
  const out = [];
  for (const o of (sub.scoring_result?.override_rules_triggered || [])) {
    if (o.action !== 'AUTO_REFER') continue;
    if (o.code === 'SIZE_CAP' && isField(o.rule) && o.rule in sd) out.push({ field: o.rule, type: 'number', label: titleOf(o.rule), why: o.desc });
    if (o.rule === 'CLAIMS_REFER') { out.push({ field: 'claims_count', type: 'number', label: 'Claims in the period', why: o.desc }); out.push({ field: 'claims_total_value', type: 'number', label: 'Total claims value ($)', why: o.desc }); }
    if (o.rule === 'PREMIUM_OUT_OF_BAND') for (const f of ['sum_insured', 'annual_turnover', 'employee_count']) if (f in sd) out.push({ field: f, type: 'number', label: titleOf(f), why: o.desc });
  }
  const seen = new Set();
  return out.filter((q) => (seen.has(q.field) ? false : seen.add(q.field)));
};

function QuestionInput({ q, value, onChange }) {
  const id = `broker-q-${q.field}`;
  if (q.type === 'boolean') {
    return (
      <div className="flex gap-2" data-testid={id}>
        {[['yes', true], ['no', false]].map(([lbl, v]) => (
          <button key={lbl} type="button" onClick={() => onChange(v)} data-testid={`${id}-${lbl}`}
            className="px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors"
            style={value === v ? { background: C.blue, color: '#fff', borderColor: C.blue } : { borderColor: 'rgba(136,150,168,0.35)', color: C.slate }}>
            {lbl === 'yes' ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === 'select') {
    return (
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inputCls} style={inputStyle} data-testid={id}>
        <option value="">Choose…</option>
        {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (q.type === 'number') {
    return <input type="number" min="0" value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls} font-mono`} style={inputStyle} data-testid={id} />;
  }
  return <textarea rows={2} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inputCls} style={inputStyle} data-testid={id} placeholder="A sentence or two" />;
}

function AnswerForm({ sub, questions, api, onUpdated, submitLabel }) {
  const sd = sub.submission_data || {};
  const [answers, setAnswers] = useState(() => Object.fromEntries(questions.map((q) => [q.field, q.type === 'number' && sd[q.field] != null ? sd[q.field] : undefined])));
  const [busy, setBusy] = useState(false);
  const filled = Object.fromEntries(Object.entries(answers).filter(([, v]) => v !== undefined && v !== ''));
  const ready = Object.keys(filled).length > 0;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/submissions/${sub.submission_id}/answer`, { answers: filled });
      if (data.policy_id) toast.success(`Bound — policy ${data.policy_number || data.policy_id}`);
      else if (data.status === 'info_requested') toast.message(data.broker_action === 'answer' ? 'Thanks — one more thing is needed' : 'Re-priced — an underwriter is still needed');
      else if (data.status === 'declined') toast.error('Re-priced — declined');
      else toast.success(`Re-priced — ${data.decision}`);
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not re-price');
    }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="broker-answer-form">
      {questions.map((q) => (
        <div key={q.field}>
          <label className="text-xs font-medium block mb-1" style={{ color: C.navy }}>{q.label}</label>
          {q.hint && <p className="text-[11px] mb-1" style={{ color: C.slate }}>{q.hint}</p>}
          <QuestionInput q={q} value={answers[q.field]} onChange={(v) => setAnswers((a) => ({ ...a, [q.field]: v }))} />
        </div>
      ))}
      <Button size="sm" disabled={busy || !ready} onClick={send} className="rounded-sm text-xs text-white" style={{ background: C.blue }} data-testid="broker-answer-submit">
        {busy ? 'Re-pricing…' : submitLabel}
      </Button>
    </div>
  );
}

function ReferForm({ sub, api, onUpdated, label }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/submissions/${sub.submission_id}/refer-to-underwriter`, { note });
      toast.success('Referred — an underwriter will pick it up');
      onUpdated?.({ ...sub, status: 'referred', broker_action: undefined, broker_may_refer: undefined, referred_by_broker: { note } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not refer');
    }
    setBusy(false);
  };
  if (!open) {
    return <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="rounded-sm text-xs" data-testid="broker-refer-btn"><UserCheck className="w-3.5 h-3.5 mr-1" />{label}</Button>;
  }
  return (
    <div className="space-y-2" data-testid="broker-refer-form">
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} style={inputStyle} placeholder="Tell the underwriter why this one deserves a look" data-testid="broker-refer-note" />
      <div className="flex gap-2">
        <Button size="sm" disabled={busy || note.trim().length < 5} onClick={send} className="rounded-sm text-xs text-white" style={{ background: C.amber }} data-testid="broker-refer-submit">{busy ? 'Referring…' : 'Send to underwriter'}</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="rounded-sm text-xs">Cancel</Button>
      </div>
    </div>
  );
}

// The broker's side of a quote that came back to them: answer, correct, or refer by hand.
export const BrokerReturnPanel = ({ sub, api, onUpdated, isStaff, showOpen = true }) => {
  const navigate = useNavigate();
  const [amending, setAmending] = useState(false);
  const questions = sub.broker_questions || [];
  const reasons = sub.return_reasons || [];
  const amendable = useMemo(() => amendableFields(sub), [sub]);
  const returned = sub.status === 'info_requested' && !!sub.broker_action;
  const declinedMayRefer = sub.status === 'declined' && !!sub.broker_may_refer;
  if (!returned && !declinedMayRefer) return null;

  if (isStaff) {
    return (
      <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: C.amber }} data-testid="broker-return-staff">
        <p className="text-xs font-semibold" style={{ color: C.navy }}>
          {declinedMayRefer ? 'Declined on claims — the broker may refer it by hand.' : sub.broker_action === 'answer' ? 'Waiting on the broker — questions asked in their quote screen.' : 'Returned to the broker — an underwriter is needed and it is their call to refer it.'}
        </p>
        <ul className="mt-1 list-disc pl-4 text-xs" style={{ color: C.slate }}>
          {questions.map((q) => <li key={q.field}>{q.label}</li>)}
          {reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
        {sub.info_expires_at && <p className="text-[11px] mt-1" style={{ color: C.slate }}>Expires {new Date(sub.info_expires_at).toLocaleDateString('en-AU')} if nothing comes back.</p>}
      </Card>
    );
  }

  if (declinedMayRefer) {
    return (
      <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: C.red }} data-testid="broker-return-declined">
        <p className="text-xs" style={{ color: C.slate }}>Declined automatically on claims history. If there is more to the story, an underwriter can look at it — your call.</p>
        <div className="mt-2"><ReferForm sub={sub} api={api} onUpdated={onUpdated} label="Refer to an underwriter anyway" /></div>
      </Card>
    );
  }

  if (sub.broker_action === 'answer') {
    return (
      <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: C.amber }} data-testid="broker-return-answer">
        <div className="flex items-start gap-2.5">
          <MessageSquareText className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.amber }} />
          <div className="text-xs flex-1" style={{ color: C.navy }}>
            <p className="font-semibold">Nearly there — {questions.length === 1 ? 'one answer is' : `${questions.length} answers are`} needed before this can bind.</p>
            <p className="mt-0.5 mb-3" style={{ color: C.slate }}>Answer here and the quote re-prices on the spot. If it passes, it binds and the documents issue — no underwriter in between.</p>
            <AnswerForm sub={sub} questions={questions} api={api} onUpdated={onUpdated} submitLabel="Answer & re-price" />
            {sub.info_expires_at && <p className="text-[11px] mt-2" style={{ color: C.slate }}>Open until {new Date(sub.info_expires_at).toLocaleDateString('en-AU')}.</p>}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: C.amber }} data-testid="broker-return-refer">
      <div className="flex items-start gap-2.5">
        <UserCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.amber }} />
        <div className="text-xs flex-1" style={{ color: C.navy }}>
          <p className="font-semibold">An underwriter is needed for this one — your call.</p>
          {reasons.length > 0 && <ul className="mt-1 list-disc pl-4" style={{ color: C.slate }} data-testid="broker-return-reasons">{reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>}
          <p className="mt-1" style={{ color: C.slate }}>Refer it and an underwriter picks it up from the queue, or correct a detail and re-price it yourself. Nothing happens until you choose.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <ReferForm sub={sub} api={api} onUpdated={onUpdated} label="Refer to underwriter" />
            {(amendable.length > 0 || questions.length > 0) && !amending && (
              <Button size="sm" variant="outline" onClick={() => setAmending(true)} className="rounded-sm text-xs" data-testid="broker-amend-btn"><PencilLine className="w-3.5 h-3.5 mr-1" />Correct details & re-price</Button>
            )}
            {showOpen && <Button size="sm" variant="ghost" onClick={() => navigate(`/submissions/${sub.submission_id}`)} className="rounded-sm text-xs" data-testid="broker-return-open-quote"><ShieldCheck className="w-3.5 h-3.5 mr-1" />Open the quote</Button>}
          </div>
          {amending && (
            <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
              <AnswerForm sub={sub} questions={[...questions, ...amendable]} api={api} onUpdated={onUpdated} submitLabel="Re-price with these details" />
            </div>
          )}
          {sub.info_expires_at && <p className="text-[11px] mt-2" style={{ color: C.slate }}>Open until {new Date(sub.info_expires_at).toLocaleDateString('en-AU')} — then it expires.</p>}
        </div>
      </div>
    </Card>
  );
};
